import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '@/redux/asyncThunks/notification'
import NotificationPanel from './NotificationPanel'
import NotificationPopup from './NotificationPopup'
import {
  NotificationDisplayType,
  NotificationType,
  Notification,
} from '@/redux/types/notifications'
import { toast } from 'sonner'

const NotificationIcon: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { unreadCount, notifications } = useSelector(
    (state: RootState) => state.notifications
  )
  const [isOpen, setIsOpen] = useState(false)
  const [centerModalNotification, setCenterModalNotification] = useState<Notification | null>(null)
  const shownToastIds = useRef<Set<number>>(new Set())

  const panelNotifications = useMemo(
    () =>
      notifications.filter((n) => {
        if (n.type_code === NotificationType.EVENT_RELEASED) return false
        if (
          n.type_code === NotificationType.SYSTEM_MESSAGE &&
          (n.display_type ===
            NotificationDisplayType.MODAL_CENTER_AUTO_DISMISS ||
            n.display_type === NotificationDisplayType.POPUP_BOTTOM_RIGHT)
        )
          return false
        return true
      }),
    [notifications]
  )

  const handleMarkAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsRead())
  }, [dispatch])

  useEffect(() => {
    const newPopups = notifications.filter(
      (n) =>
        !n.is_read &&
        !shownToastIds.current.has(n.id) &&
        (n.type_code === NotificationType.EVENT_RELEASED ||
          (n.type_code === NotificationType.SYSTEM_MESSAGE &&
            n.display_type === NotificationDisplayType.POPUP_BOTTOM_RIGHT))
    )

    newPopups.forEach((n) => {
      shownToastIds.current.add(n.id)

      const markRead = () =>
        dispatch(
          markNotificationAsRead({
            notificationId: n.id,
            type_code: n.type_code,
            displayType: n.display_type,
          })
        )

      toast.custom(
        (toastId) => (
          <NotificationPopup
            title={n.title}
            message={n.message}
            displayType={n.display_type}
            type={n.data?.variant}
            onClose={() => {
              toast.dismiss(toastId)
              markRead()
            }}
          />
        ),
        {
          id: String(n.id),
          duration: 5000,
          onAutoClose: markRead,
          closeButton: false,
        }
      )
    })
  }, [notifications, dispatch])

  useEffect(() => {
    const unreadCenterModal = notifications.find(
      (n) =>
        !n.is_read &&
        n.type_code === NotificationType.SYSTEM_MESSAGE &&
        n.display_type === NotificationDisplayType.MODAL_CENTER_AUTO_DISMISS
    )

    if (
      unreadCenterModal &&
      (!centerModalNotification ||
        centerModalNotification.id !== unreadCenterModal.id)
    ) {
      setCenterModalNotification(unreadCenterModal)
    } else if (!unreadCenterModal && centerModalNotification) {
      setCenterModalNotification(null)
    }
  }, [notifications, centerModalNotification])

  const handleCloseCenterModal = useCallback(() => {
    if (centerModalNotification) {
      dispatch(
        markNotificationAsRead({
          notificationId: centerModalNotification.id,
          type_code: centerModalNotification.type_code,
          displayType: centerModalNotification.display_type,
        })
      )
      setCenterModalNotification(null)
    }
  }, [centerModalNotification, dispatch])

  useEffect(() => {
    if (centerModalNotification) {
      const timer = setTimeout(() => {
        handleCloseCenterModal()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [centerModalNotification, handleCloseCenterModal])

  return (
    <>
      {centerModalNotification && (
        <NotificationPopup
          title={centerModalNotification.title}
          message={centerModalNotification.message}
          displayType={centerModalNotification.display_type}
          type={centerModalNotification?.data?.variant}
          onClose={handleCloseCenterModal}
        />
      )}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label='Notifications'
            className='relative inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors hover:bg-[#02357217] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          >
            <img
              src='/icons/bell.svg'
              alt=''
              className='h-[1.38rem] w-[1.31rem]'
            />
            {unreadCount > 0 && (
              <div className='absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white'>
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-80 max-h-[28rem] overflow-hidden flex flex-col p-0 bg-[#D5DEEBD9] border border-[#E6ECF2] backdrop-blur-[10px]'
          align='end'
        >
          <NotificationPanel
            notifications={panelNotifications}
            onMarkAllAsRead={handleMarkAllAsRead}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export default NotificationIcon
